/**
 * Chip - the small rounded pill that stands in for one selected thing.
 *
 * Atomic Design role: Atom. A label, an optional × that removes it, and
 * nothing else. The caller owns the list the chip came out of.
 *
 * Every multi-select in this app ends the same way: the picker adds a value,
 * and the values already picked sit under it as pills you can dismiss. That
 * markup had been retyped per feature — the additional-assignee chips in
 * AssigneeFieldset, the selected-company pills on the Admin user form — with
 * the usual drift in padding, border, and whether the × was a real touch
 * target. One atom, so a pill reads the same wherever it appears.
 *
 * The × keeps its 14px glyph and reaches the 44px floor with the transparent
 * pseudo-element trick (CONVENTIONS §5, option 2): a chip row is dense enough
 * that a real 44px box would shove the pills apart.
 *
 * @example
 * <Chip label="GBrands Facility Services" onRemove={() => drop(id)} />
 *
 * @example  // static, no dismiss
 * <Chip label="Subcontractor" />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface ChipProps {
	/** Text inside the pill. */
	label: React.ReactNode;
	/** Given, renders the × and calls this when it's pressed. Omit for a static pill. */
	onRemove?: () => void;
	/**
	 * Accessible name for the ×. Defaults to `Remove <label>` when the label is
	 * a plain string; pass it explicitly when the label is a node, otherwise the
	 * button reaches a screen reader as an unnamed "button".
	 */
	removeAriaLabel?: string;
	/** Extra classes merged onto the root. */
	className?: string;
	/** Overrides the atom's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const Chip: React.FC<ChipProps> = ({ label, onRemove, removeAriaLabel, className = '', dataRef = 'atom-chip' }) => {
	/* Only a string label can be folded into "Remove X" automatically — a node
	   has no reliable text, so it falls back to the bare verb. */
	const ariaLabel = removeAriaLabel ?? (typeof label === 'string' ? `Remove ${label}` : 'Remove');

	return (
		/* max-w-full + a breakable label: a pill row is flex-wrap, but a single
		   long unbroken label (a full company name, an email) would still push
		   its row past the container instead of wrapping inside the pill. */
		<span className={`chip inline-flex items-center gap-4px px-10px py-4px rounded-full bg-brand-surface text-14px font-sans text-brand-text border border-brand-border max-w-full ${className}`} data-ref={dataRef}>
			<span className="chip__label min-w-0 break-words">{label}</span>
			{onRemove && (
				<button type="button" onClick={onRemove} className="chip__remove relative ml-2px inline-flex items-center text-brand-text-muted hover:text-red-600 transition-colors before:absolute before:-inset-12px before:content-['']" aria-label={ariaLabel}>
					<svg className="h-14px w-14px shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
				</button>
			)}
		</span>
	);
};

export default Chip;
